export interface PublicUser {
  id: number;
  telegramId: string;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  photoUrl: string | null;
  displayName: string;
  /** Имя выбрано вручную, а не взято из Telegram. */
  hasCustomName?: boolean;
  avatar: {
    kind: 'telegram' | 'emoji' | 'image';
    emoji: string | null;
    image: string | null;
  };
  tier: 'trial' | 'free' | 'premium';
  /** Владелец проекта: только он видит расходы на модель. */
  isOwner: boolean;
  isPremium: boolean;
  trial: { active: boolean; endsAt: string | null; daysLeft: number };
  /** frozen — премиальное было и закончилось: предлагать «продли», а не «попробуй». */
  subscription: { active: boolean; until: string | null; frozen: boolean };
  createdAt: string;
  settings: {
    theme: 'auto' | 'light' | 'dark';
    locale: string;
    decompositionMode: 'auto' | 'ask' | 'never';
    reduceMotion: boolean;
    weeklyRecap: boolean;
    onboardingState: 'pending' | 'done' | 'skipped';
    tzOffsetMinutes: number;
    /** Три выключателя уведомлений: поводы отдельно, канал отдельно. */
    notifications: {
      invites: boolean;
      friendPosts: boolean;
      inBot: boolean;
      reminders?: boolean;
    };
    privacy: {
      profileVisibility: 'public' | 'followers' | 'private';
      showStats: boolean;
      showBadges: boolean;
      showStreak: boolean;
    };
  } | null;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /**
   * Человеческое объяснение от сервера, если оно есть.
   *
   * Нужно там, где отказ обязан объяснять ценность, а не сообщать код:
   * «Участие в событии недели — часть подписки» вместо `premium_required`.
   */
  readonly detail: string | null;

  constructor(status: number, code: string, detail: string | null = null) {
    super(code);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

/**
 * Человеческое объяснение неудачи.
 *
 * Общий `catch { setError('Не удалось…') }` уже трижды сбивал с толку: он
 * одинаково выглядит и когда пропала связь, и когда сервер отказал по делу.
 * Человек видит «не работает» там, где на самом деле «нет интернета», и
 * ищет ошибку в приложении.
 *
 * Поэтому причина называется прямо, а `fallback` остаётся только для того,
 * чего мы действительно не разобрали.
 */
export function describeError(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback;

  if (err.code === 'network_unavailable') {
    return 'Нет связи с сервером. Проверь интернет и попробуй ещё раз.';
  }
  if (err.status === 429) {
    return 'Слишком часто. Подожди несколько секунд.';
  }
  if (err.status === 401) {
    return 'Сессия истекла. Открой приложение заново из бота.';
  }
  // Сервер прислал объяснение — оно точнее любого нашего.
  return err.detail ?? fallback;
}

const TOKEN_KEY = 'lvxp.session';
const DEMO_KEY = 'lvxp.session.demo';

/**
 * Сессия — своя или песочницы.
 *
 * Песочница владельца лежит ОТДЕЛЬНЫМ ключом и просто перекрывает основной,
 * пока включена. Из этого следуют два нужных свойства: выход из песочницы
 * возвращает человека в свой аккаунт мгновенно и без повторного входа,
 * а протухшая сессия песочницы не выкидывает его из собственной.
 *
 * Записи (`set`) всегда идут в ОСНОВНОЙ ключ: токен песочницы выдаёт только
 * сервер по явной просьбе, и обычный вход не должен его затирать.
 */
export const session = {
  get: () => {
    try {
      return localStorage.getItem(DEMO_KEY) ?? localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (t: string) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
  /** Войти в песочницу. Свой токен остаётся на месте и ждёт возвращения. */
  useDemo: (t: string) => localStorage.setItem(DEMO_KEY, t),
  dropDemo: () => localStorage.removeItem(DEMO_KEY),
  inDemo: () => {
    try {
      return localStorage.getItem(DEMO_KEY) !== null;
    } catch {
      return false;
    }
  },
};

const BASE = import.meta.env['VITE_API_BASE'] ?? '';

/**
 * Как заново войти, если сессия оказалась негодной.
 *
 * Регистрируется извне (auth.ts), а не импортируется сюда: иначе два модуля
 * начнут импортировать друг друга по кругу.
 */
let relogin: (() => Promise<boolean>) | null = null;
let reloginInFlight: Promise<boolean> | null = null;

export function setRelogin(fn: () => Promise<boolean>): void {
  relogin = fn;
}

/**
 * Один вход на всех.
 *
 * При запуске в воздухе висит несколько запросов сразу. Если сессия
 * протухла, 401 прилетит каждому — и каждый полезет логиниться заново.
 * Общее обещание превращает пять входов в один.
 */
function reauthenticate(): Promise<boolean> {
  if (!relogin) return Promise.resolve(false);
  reloginInFlight ??= relogin().finally(() => {
    reloginInFlight = null;
  });
  return reloginInFlight;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export async function api<T>(
  path: string,
  options: { method?: string; body?: unknown; auth?: boolean; retry?: boolean } = {},
): Promise<T> {
  const { method = 'GET', body, auth = true, retry = true } = options;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth) {
    const token = session.get();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    /*
     * Одна повторная попытка на чтении.
     *
     * Приложение живёт на туннеле и на телефоне: одиночный обрыв соединения
     * тут — обычное дело, а «Не удалось загрузить» после него читается как
     * поломка приложения. Повторяем только GET: он ничего не меняет, и
     * повторить его безопасно. POST и DELETE не повторяем никогда —
     * второй такой запрос может сделать работу дважды.
     */
    if (retry && method === 'GET') {
      await sleep(400);
      return api<T>(path, { ...options, retry: false });
    }
    throw new ApiError(0, 'network_unavailable');
  }

  if (!response.ok) {
    let code = `http_${response.status}`;
    let detail: string | null = null;
    try {
      const data = (await response.json()) as { error?: string; message?: string };
      if (data.error) code = data.error;
      // Сообщение отдаём наверх, только если оно отличается от кода: иначе
      // интерфейс покажет `premium_required` вместо человеческой фразы.
      if (data.message && data.message !== code) detail = data.message;
    } catch {
      /* тело может быть пустым */
    }
    /*
     * 401 — это не повод показывать человеку «Сессия истекла».
     *
     * Внутри Telegram у нас всегда есть подписанный initData, то есть войти
     * заново можно молча и мгновенно. Раньше приложение вместо этого писало
     * «Открой приложение заново из бота» — то есть просило человека сделать
     * руками ровно то, что умеет само.
     *
     * Просьба показывается, только если и повторный вход не удался: тогда
     * дело действительно не в токене.
     */
    if (response.status === 401) {
      /*
       * Сломанная песочница возвращает домой, а не выкидывает из аккаунта.
       * Свой токен при этом не трогаем: он ни в чём не виноват.
       */
      if (session.inDemo()) {
        session.dropDemo();
      } else {
        session.clear();
      }
      if (auth && retry && (await reauthenticate())) {
        return api<T>(path, { ...options, retry: false });
      }
    }
    throw new ApiError(response.status, code, detail);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Запрос «на выходе»: страницу закрывают, а он должен дойти. `keepalive`
 * держит его живым после выгрузки страницы; ответ никто не ждёт.
 */
export function farewell(path: string, method = 'DELETE'): void {
  const token = session.get();
  try {
    void fetch(`${BASE}${path}`, {
      method,
      keepalive: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).catch(() => undefined);
  } catch {
    /* страница уже выгружается — сервер уберёт по отсутствию отметок */
  }
}
